import classNames from 'classnames';
import type { FC, ReactHTML, ReactNode } from 'react';
import {
  Tooltip,
  type TooltipPlacementSide,
  isOverflowingContent,
} from './Tooltip';

type TextOverflowTooltipProps = {
  as?: keyof ReactHTML;
  children: ReactNode;
  className?: string;
  placement?: TooltipPlacementSide;
  /**
   * props.children is used as tooltip content by default, but you can
   * override that using this prop.
   */
  content?: ReactNode;
};

/**
 * A tooltip that shows up if wrapped text doesn't fit in the element
 *
 * The element must have limited width to work properly and ideally contain
 * just plaintext inside.
 *
 * Variants wrapping more than plaintext may or may not work, you need to
 * experiment when you need such thing. For example, we needed a badge followed
 * by text in one case. The badge was truncated all or none and had to be handled
 * another way, but text after the badge was truncated fine.
 *
 * Can be used on any HTML element, feel free to add variants as needed.
 */
export const TextOverflowTooltip: FC<TextOverflowTooltipProps> = (props) => {
  const Element = props.as ?? 'div';
  return (
    <Tooltip
      placement={props.placement}
      shouldShow={isOverflowingContent}
      content={props.content ?? props.children}
    >
      <Element className={classNames(props.className, 'truncate')}>
        {props.children}
      </Element>
    </Tooltip>
  );
};
